import {
  CommandRegularIcon,
  MagnifyingGlassRegularIcon,
  ListRegularIcon,
  CalendarIcon,
} from "@elixir/icons";
import { getAclViewClassName, useLocalStorage } from "@elixir/utils";
import moment from "dayjs";
import { useEffect, useState } from "react";
import { InputField, TooltipComponent } from "ui";
import { shallow } from "zustand/shallow";
import { ReactComponent as ArrowUpRightFromSquareRegularIcon } from "../../assets/icons/font-awesome/arrow-up-right-from-square-regular.svg";
import CustomDatePicker from "../../molecules/chips/custom-date-picker";
import useQueueStore from "../../stores/queue-store";
import { TLeftQueuePanel } from "../../stores/queue-store/types";
import getBridges from "../../utils/bridges/get-bridges";
import { openUniversalPatientSearch } from "../../utils/bridges/methods";
import { track } from "../../utils/tracking";
import ClinicSelector from "./clinic-selector";
import { QUEUE_TYPE, SUBJECT_KEYS } from "@elixir/types";
import getB from "../../utils/bridges/EkaBridge";
import GroupSelector from "./group-selector";

const DISABLE_OLD_VIEW_ONBOARDING_DATE = new Date("2024-04-16T18:30:00.000Z");

export const navigateLocal = (
  clinicId: string,
  left: TLeftQueuePanel = "add-new",
  right:
    | QUEUE_TYPE.CHECKED_IN
    | QUEUE_TYPE.COMPLETED
    | string = QUEUE_TYPE.CHECKED_IN
) => {
  const { navigate } = getBridges() || {};
  const params = new URLSearchParams(window.location.search);
  const _left = left || params.get("left");
  const _right = right || params.get("right");
  const _params = window.location.search
    .slice(1)
    .split("&")
    .filter(Boolean)
    .filter((param) => {
      const [key, value] = param.split("=");
      if (key === "left" || key === "right") return false;
      return true;
    })
    .filter(Boolean)
    .join("&");
  const _url = `/app/appointments/clinic/${clinicId}/queue?left=${_left}&right=${_right}&${_params}`;
  navigate("replace", _url);
};
// type QueueHeaderProps = {
//   clinicIdFromParent?: string;
// };

const QueueHeader = () => {
  const [_, setNewQueue] = useLocalStorage("isNewView", "true");
  const { navigate, isInBetaSchedule } = getBridges() || {};
  const [showScheduleSwitch, setShowScheduleSwitch] = useState(false);
  const [isGroupEnabled, setIsGroupEnabled] = useState(
    JSON.parse(localStorage.getItem("myProfile") || "{}")?.group?.name.trim()
      .length > 0
  );

  useEffect(() => {
    isInBetaSchedule?.().then((isBeta) => {
      setShowScheduleSwitch(isBeta);
    });

    getB()
      ?.getIdb()
      ?.misc?.where("key")
      .equals("groupAccess")
      .first()
      .then((t) => {
        setIsGroupEnabled(t?.groupAccess?.enabled);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    openLeftPanel,
    setOpenLeftPanel,
    selectedDate,
    setSelectedDate,
    clinicId,
    setClinicId,
  } = useQueueStore(
    ({
      openLeftPanel,
      setOpenLeftPanel,
      selectedDate,
      setSelectedDate,
      clinicId,
      setClinicId,
    }) => ({
      setClinicId,
      openLeftPanel,
      setOpenLeftPanel,
      selectedDate,
      setSelectedDate,
      clinicId,
    }),
    shallow
  );

  // const { setClinicId, cid = clinicId } = useQueueStore(
  //   ({ setClinicId, clinicId }) => ({
  //     setClinicId,
  //     cid: clinicId,
  //   }),
  //   shallow
  // );

  const handleDateChange = (dateValue: moment.Dayjs) => {
    track("queue_date_change", {});
    const dateObj = dateValue.startOf("day").toDate();
    setSelectedDate(dateObj);
  };

  const isCurrentSelectedDateToday =
    selectedDate && moment(selectedDate).isSame(moment(Date.now()), "date");

  return (
    <div
      className="w-full flex border-b-1 dr-py-8 dr-px-16 dr-border-b-darwin-touch-neutral-200 items-center justify-between"
      // style={{
      //   height: `2.5rem`,
      // }}
    >
      <div className="flex items-center gap-x-8">
        {!isGroupEnabled && (
          <div className="Title3Regular dr-text-darwin-touch-neutral-1000">
            Queue
          </div>
        )}
        {openLeftPanel ? (
          <img
            src="https://cdn.eka.care/vagus/clm60rsv1003r0thuagz2526u.webp"
            onClick={() => setOpenLeftPanel(false)}
            className={`dr-block dr-w-32 dr-h-32 dr-cursor-pointer ${getAclViewClassName(SUBJECT_KEYS.LEFT_BLOCK_ON_NEW_QUEUE)}`}
          />
        ) : (
          <img
            src="https://cdn.eka.care/vagus/clmyircnr00010tik2jiobajd.png"
            onClick={() => setOpenLeftPanel(true)}
            className={`dr-block dr-w-32 dr-h-32 dr-cursor-pointer ${getAclViewClassName(SUBJECT_KEYS.LEFT_BLOCK_ON_NEW_QUEUE)}`}
          />
        )}
        <CustomDatePicker
          isToday={isCurrentSelectedDateToday}
          onChange={handleDateChange}
          value={moment(selectedDate)}
        />
        <button
          id="calendar-today"
          onClick={() => {
            track("queue_date_change_today", {});
            handleDateChange(moment(Date.now()));
          }}
          disabled={isCurrentSelectedDateToday}
          className={`dr-max-w-min BodyRegular dr-space-x-8 flex items-center dr-h-36 dr-px-12 dr-rounded-8  dr-border-none dr-bg-darwin-touch-neutral-50`}
        >
          <span
            className={`${
              isCurrentSelectedDateToday
                ? "dr-text-darwin-touch-neutral-1000"
                : "dr-text-darwin-touch-primary-primary"
            }`}
          >
            Today
          </span>
        </button>
        <style>{`
          #calendar-today:disabled{
            background: #F4F4F4 !important;
          }
          #calendar-today{
            background: #E9EFFF !important;
          }

          `}</style>
        {isGroupEnabled ? <GroupSelector /> : null}
        <ClinicSelector
          selectedClinicId={clinicId}
          onClinicChange={(id) => {
            track("queue_clinic_change", {});
            navigateLocal(id);
            setClinicId(id);
          }}
          showAllClinicsOption
        />
        {/* <div id="clinic_selector_menu"></div> */}
        {showScheduleSwitch && (
          <div
            className={`dr-flex dr-bg-darwin-touch-neutral-50 dr-p-2 dr-rounded-8 ${getAclViewClassName(SUBJECT_KEYS.APPOINTMENTS_CALENDAR_VIEW)}`}
          >
            <div
              className="p-8 bg-white rounded-6 dr-cursor-pointer"
              style={{ boxShadow: "0 0 8px rgba(0, 0, 0, 0.12)" }}
            >
              <ListRegularIcon width={14} />
            </div>
            <div
              className="p-8 dr-cursor-pointer"
              onClick={() => {
                navigate?.(
                  "push",
                  `/app/beta/schedule/${
                    clinicId === "all"
                      ? localStorage.getItem("selectedClinicId")
                      : clinicId
                  }`
                );
              }}
            >
              <CalendarIcon width={14} />
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center space-x-8 pl-8">
        <InputField
          onClick={() => {
            openUniversalPatientSearch();
          }}
          iconPosition="left"
          IconComponent={SearchIcon}
          placeholder="Search"
          customSuffix={
            <div className="dr-text-darwin-touch-neutral-600 dr-flex dr-items-center dr-space-x-1 BodyRegular dr-px-12">
              <CommandRegularIcon className="dr-text-darwin-touch-neutral-600 dr-w-16 dr-h-16 dr-p-2" />
              <span>+K</span>
            </div>
          }
          className={`dr-max-w-180 ${getAclViewClassName(SUBJECT_KEYS.GLOBAL_SEARCH)}`}
        />
        {/* <DownloadQueue /> */}
        <CustomButtonComponent />
        {/* <div className="dr-px-8 dr-py-6 dr-rounded-8 flex items-center TouchFootnoteBold dr-bg-darwin-touch-neutral-50 dr-space-x-8 dr-h-36 dr-min-w-200">
          <div className="flex items-center space-x-8">
            {!!pic_small ? (
              <img
                loading="lazy"
                src={pic_small}
                className="w-24 h-24 dr-min-w-24 dr-rounded-4 dr-overflow-hidden"
              />
            ) : null}

            <div className="dr-text-darwin-touch-neutral-1000 dr-line-clamp-1">
              {fullName}
            </div>
          </div>
          <ChevronDownRegularIcon
            className={`w-16 dr-transition-all dr-ease-in-out`}
          />
        </div> */}
        {/* <div className="dr-p-6 dr-bg-darwin-touch-neutral-50 dr-rounded-8 flex justify-center items-center dr-cursor-pointer">
          <ShuffleRegularRegularIcon className="w-24 dr-min-w-24 h-24 dr-p-4 dr-text-darwin-touch-neutral-1000" />
        </div> */}
        {/* <Input
          className="search-appointment border-1 dr-border-darwin-touch-neutral-100 dr-rounded-8 dr-bg-darwin-touch-neutral-50"
          placeholder="Search Appointments"
          suffix={<MagnifyingGlassRegularIcon />}
        /> */}
        <style>{`
          .search-appointment > input {
            background: transparent;
          }
        `}</style>
      </div>
    </div>
  );
};

export default QueueHeader;

export const CustomButtonComponent = () => {
  useEffect(() => {
    if (window.renderSwitchProfileButton) {
      window.renderSwitchProfileButton({ containerId: "change_doctor" });
    }
    if (window.renderProfileButton) {
      window.renderProfileButton({ containerId: "doctor_menu_dropdown" });
    }
  }, []);

  return (
    <>
      <div id="doctor_menu_dropdown"></div>
      <div
        id="change_doctor"
        className={getAclViewClassName(SUBJECT_KEYS.SWITCH_WORKSPACE)}
      ></div>
    </>
  );
};

const SearchIcon = () => (
  <MagnifyingGlassRegularIcon className="dr-text-darwin-touch-neutral-800" />
);
